import ForgotPasswordActions from '~/src/components/auth/forgot-password/ForgotPasswordActions'
import ForgotPasswordFields from '~/src/components/auth/forgot-password/ForgotPasswordFields'
import useAskResetPassword from '~/src/hooks/auth/useAskResetPassword'
import { PasswordForgotFormInterface } from '~/src/types/forms/PasswordForgotFormInterface'
import { FormikProps, useFormik } from 'formik'
import * as yup from 'yup'
import ValidatorUtils from '~/src/utils/ValidatorUtils'
import { AUTHENTICATION_FLOW_STATE, AuthenticationFlowState } from '~/src/stores/authentication-flow'
import { useEoValue } from '~/src/hooks/useEoState'
import { AUTHENTICATION_STATE, AuthenticationState } from '~/src/stores/authentication'
import { useTranslations } from '~/src/hooks/useTranslations'
import { default as parsePhoneNumber } from 'libphonenumber-js'

interface Props {
  fieldsColor?: 'container' | 'content',
  onClose?(): void,
}

const _clearPhoneNumber = (value: string | null | undefined): string | null => {
  if (!value) {
    return null
  }

  return parsePhoneNumber(value)?.number || null
}

const ForgotPasswordForm = ({ fieldsColor = 'content', onClose }: Props) => {
  const { t } = useTranslations()
  const { reset, loading } = useAskResetPassword()

  const authenticationState = useEoValue<AuthenticationState>(AUTHENTICATION_STATE)
  const authenticationFlowState = useEoValue<AuthenticationFlowState>(AUTHENTICATION_FLOW_STATE)
  const formik: FormikProps<PasswordForgotFormInterface> = useFormik<PasswordForgotFormInterface>({
    initialValues: {
      email: authenticationState.guest?.email || authenticationFlowState.customer?.email || '',
      phone: authenticationState.guest?.phone || authenticationFlowState.customer?.phone || ''
    },
    validationSchema: yup.object({
      email: yup
        .string()
        .email(t('errors.emailInvalid'))
        .test('email-or-phone', t('errors.required'), function (value: string | undefined) {
          const { phone } = this.parent
          const phoneNumber = _clearPhoneNumber(phone)

          if (!value && !phoneNumber) {
            return this.createError({
              path: 'email',
              message: t('errors.required')
            })
          }

          if (value && phoneNumber) {
            return this.createError({
              path: 'email',
              message: t('errors.required')
            })
          }

          return true
        }),
      phone: yup
        .string()
        .test('phone-or-email', t('errors.required'), function (value: string | undefined) {
          const { email } = this.parent
          const phoneNumber = _clearPhoneNumber(value)

          if (!phoneNumber && !email) {
            return this.createError({
              path: 'phone',
              message: t('errors.required')
            })
          }

          if (phoneNumber && email) {
            return this.createError({
              path: 'phone',
              message: t('errors.required')
            })
          }

          if (phoneNumber && !ValidatorUtils.isPhoneNumberValid(value)) {
            return this.createError({
              path: 'phone',
              message: t('errors.phoneWrongFormat')
            })
          }

          return true
        })
    }),
    onSubmit: (values => {
      const email: string | null = values.email || null
      const phone: string | null = _clearPhoneNumber(values.phone) || null
      
      reset({
        email,
        phone
      })
        .then(() => {
          onClose && onClose()
        })
        .catch(() => {
          //
        })
    })
  })

  return (
    <form onSubmit={formik.handleSubmit}>
      <ForgotPasswordFields
        form={formik}
        fieldsColor={fieldsColor}/>

      <ForgotPasswordActions loading={loading} />
    </form>
  )
}

export default ForgotPasswordForm
